import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, MoreHorizontal, MapPin, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { AdLikeIcon } from "@/components/AdeIcon";
import { GiftButton } from "@/components/GiftSystem";
import { GoldenRingAvatar } from "@/components/GoldenRingAvatar";
import { MentionSuggestions, applyMention } from "@/components/MentionSuggestions";
import { CaptionRenderer } from "@/components/CaptionRenderer";
import { ProBadge } from "@/components/ProBadge";
import { PostVideo } from "@/components/PostVideo";
import { PinchZoomMedia } from "@/components/PinchZoomMedia";
import { fxLike } from "@/utils/gamefx";

const defaultTimeAgo = (dateString, t) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (mins < 1) return t("timeNow");
  if (mins < 60) return `${mins}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}${t("timeDayUnit")}`;
  return new Date(dateString).toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const CommentIcon = ({ onClick, gid, "data-testid": testId }) => (
  <button onClick={onClick} className="group" data-testid={testId}>
    <svg viewBox="0 0 24 24" className="w-7 h-7 transition-transform group-hover:scale-105 group-active:scale-95">
      <defs>
        <linearGradient id={`commentGradient-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#06B6D4" />
        </linearGradient>
      </defs>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
        fill="none" stroke={`url(#commentGradient-${gid})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const ShareIcon = ({ onClick, gid, title, "data-testid": testId }) => (
  <button onClick={onClick} className="group" title={title} data-testid={testId}>
    <svg viewBox="0 0 24 24" className="w-7 h-7 transition-transform group-hover:scale-105 group-active:scale-95">
      <defs>
        <linearGradient id={`shareGradient-${gid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" fill="none" stroke={`url(#shareGradient-${gid})`}
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
);

const GiftCounter = ({ value, total, onClick, testId }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-sm font-semibold mt-1 hover:opacity-80 transition-opacity" data-testid={testId}>
    <Gift className="w-4 h-4 text-amber-500" />
    <span className="bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent font-bold">
      {(value || total || 0) >= 1000000
        ? `${((value || total) / 1000000).toFixed(1)}M`
        : (value || total || 0) >= 1000
          ? `${((value || total) / 1000).toFixed(0)}K`
          : (value || total || 0).toLocaleString()} ADE Coins
    </span>
    <span className="text-xs text-amber-600">· {total || 0} Gift</span>
  </button>
);

/* Canonical ADE content card — renders BOTH Posts and Discussions.
   In HERE a Discussion IS a Post: same header, same action bar, same counters.
   The only differences: subtle DISCUSSION label, conversational content preview,
   and tap/comment opening the chat-style Discussion detail. */
export const PostCard = ({
  post,
  isDiscussion: isDiscussionProp,
  highlight = false,
  saved = false,
  likeAnimation = false,
  formatTimeAgo,
  onLike,
  onDoubleTapLike,
  onSave,
  onComment,
  onShare,
  onOpenLikes,
  onOpenGifts,
  onGiftSent,
  onStateChange,
}) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const isDiscussion = isDiscussionProp ?? post.content_type === "discussion";
  const cid = isDiscussion ? post.id : post.photo_id;
  const author = isDiscussion ? post.creator : post.user;
  const timeAgo = formatTimeAgo || ((d) => defaultTimeAgo(d, t));

  const [commentText, setCommentText] = useState("");
  const [carouselIdx, setCarouselIdx] = useState(0);
  // Discussion-only local interaction state (canonical Like/Gift systems)
  const [dLiked, setDLiked] = useState(!!post.is_liked);
  const [dLikes, setDLikes] = useState(post.likes_count || 0);
  const [dGifts, setDGifts] = useState(post.total_gifts || 0);
  const [dGiftValue, setDGiftValue] = useState(post.gift_value || 0);
  const [localSaved, setLocalSaved] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [joined, setJoined] = useState(!!(post.my_state?.following || post.my_state?.participant));
  // IG-style: ratio del media clampato tra 4:5 (verticale max) e 1.91:1 (orizzontale)
  const [mediaRatio, setMediaRatio] = useState(4 / 5);
  const onMediaLoad = (e) => {
    const im = e.target;
    if (!im?.naturalWidth || !im?.naturalHeight) return;
    setMediaRatio(Math.min(1.91, Math.max(4 / 5, im.naturalWidth / im.naturalHeight)));
  };

  useEffect(() => {
    setJoined(!!(post.my_state?.following || post.my_state?.participant));
  }, [post.my_state?.following, post.my_state?.participant]);

  const st = post.my_state || {};
  const doState = async (action) => {
    try {
      await api.post(`/discussions/${cid}/state`, { action });
      setMenuOpen(false);
      if (action === "abandon") setJoined(false);
      if (action === "follow") setJoined(true);
      toast.success(action === "follow" ? t("pinnedInHere") : t("doneTxt"));
      onStateChange?.();
    } catch (e) { /* noop */ }
  };
  const doReport = async () => {
    try {
      await api.post(`/discussions/${cid}/report`, { reason: "Segnalazione utente" });
      setMenuOpen(false);
      toast.success(t("reportSent"));
    } catch (e) { /* noop */ }
  };

  const toggleDiscussionLike = async () => {
    try {
      const r = await api.post(`/discussions/${cid}/like`);
      setDLiked(r.data.liked);
      setDLikes(r.data.likes_count);
    } catch (e) { /* noop */ }
  };

  const handleLikeClick = () => {
    const wasLiked = isDiscussion ? dLiked : isLiked;
    if (!wasLiked) fxLike();
    return isDiscussion ? toggleDiscussionLike() : onLike?.(cid);
  };
  const handleDoubleTap = () => {
    if (isDiscussion) { if (!dLiked) toggleDiscussionLike(); }
    else onDoubleTapLike?.(cid, post.is_liked);
  };
  const handleCommentClick = () => {
    if (isDiscussion) navigate(`/app/discussion/${cid}?focus=1`);
    else document.getElementById(`comment-input-${cid}`)?.focus();
  };
  const handleShareClick = () => {
    if (onShare) return onShare(cid);
    const link = isDiscussion ? `/app/discussion/${cid}` : `/app?post=${cid}`;
    navigator.clipboard?.writeText(`${window.location.origin}${link}`);
    toast.success(t("linkCopied"));
  };
  const submitComment = async () => {
    if (!commentText.trim()) return;
    const ok = await onComment?.(cid, commentText.trim());
    if (ok !== false) setCommentText("");
  };
  const openDiscussion = () => navigate(`/app/discussion/${cid}`);
  const openComposer = () => navigate(`/app/discussion/${cid}?focus=1`);
  const participate = async () => {
    if (!joined) {
      setJoined(true);
      try { await api.post(`/discussions/${cid}/state`, { action: "follow" }); } catch (e) { /* noop */ }
      onStateChange?.();
    }
    navigate(`/app/discussion/${cid}?focus=1`);
  };

  const handleSaveClick = async () => {
    if (onSave) return onSave(cid);
    try {
      const r = await api.post(`/photos/${cid}/save`);
      setLocalSaved(r.data.saved);
      toast.success(r.data.message);
    } catch (e) { /* noop */ }
  };
  const isSaved = onSave ? saved : localSaved;

  const likesCount = isDiscussion ? dLikes : post.likes_count;
  const isLiked = isDiscussion ? dLiked : post.is_liked;
  const totalGifts = isDiscussion ? dGifts : (post.total_gifts || 0);
  const giftValue = isDiscussion ? dGiftValue : (post.gift_value || 0);

  return (
    <article
      className={`${isDiscussion ? "feed-glass-card discussion-border mx-3" : "post-flat"} transition-all duration-500 ${highlight ? "ring-2 ring-amber-400 ring-offset-2" : ""}`}
      data-testid={isDiscussion ? `discussion-card-${cid}` : `post-${cid}`}
      data-post-id={cid}
    >
      {/* Header — identical for Post and Discussion */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="relative">
          <GoldenRingAvatar user={author} size="sm" onClick={() => navigate(`/app/user/${author.user_id}`)} />
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 border-2 border-white rounded-full z-10" />
        </div>
        <div className="flex-1">
          <button onClick={() => navigate(`/app/user/${author.user_id}`)}
            className="text-sm font-semibold text-gray-900 hover:text-gray-600 transition-colors">
            {author.name}
          </button>
          <p className="text-xs text-gray-400">
            {timeAgo(post.created_at)}
            {!isDiscussion && post.location && (
              <span className="text-gray-500 inline-flex items-center gap-1 ml-1">
                · <MapPin className="w-3 h-3" strokeWidth={2.2} /> {post.location}
              </span>
            )}
            {isDiscussion && post.approximate_location_context && (
              <span className="text-gray-500 inline-flex items-center gap-1 ml-1">
                · <MapPin className="w-3 h-3" strokeWidth={2.2} /> {post.approximate_location_context}
              </span>
            )}
          </p>
          {!isDiscussion && post.tagged_users?.length > 0 && (
            <p className="text-[11px] text-gray-500" data-testid="post-tagged-users">
              con {post.tagged_users.map((u) => u.name).join(", ")}
            </p>
          )}
        </div>
        {isDiscussion && (
          <button onClick={() => setMenuOpen(true)} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid={`discussion-menu-btn-${cid}`}>
            <MoreHorizontal className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      {isDiscussion ? (
        <div className="px-4 pb-2 cursor-pointer aspect-square flex flex-col" onClick={openDiscussion} onDoubleClick={handleDoubleTap}
          data-testid={`discussion-content-${cid}`}>
          <p className="text-[9px] font-bold tracking-[0.18em] text-pink-500/80 mb-1" data-testid={`discussion-label-${cid}`}>
            DISCUSSION
          </p>
          <h3 className="text-[15px] font-bold text-gray-900 leading-snug">{post.title}</h3>
          <p className="text-sm text-gray-700 mt-0.5 line-clamp-3">{post.opening_text}</p>
          {(() => {
            const replies = post.recent_replies || [];
            if (!replies.length) return null;
            let side = 0;
            const sides = replies.map((r, i) => {
              if (i > 0 && r.user_id !== replies[i - 1].user_id) side = 1 - side;
              return side;
            });
            return (
              <div className={`flex-1 overflow-hidden flex flex-col gap-1.5 mt-2.5 ${replies.length >= 5 ? "justify-end" : "justify-start"}`}
                data-testid={`discussion-preview-${cid}`}>
                {replies.map((r, i) => (
                  <div key={r.id || i} className={`flex items-end gap-1.5 ${sides[i] === 1 ? "flex-row-reverse" : ""}`}>
                    <img src={r.user?.picture || "/images/default-avatar.png"} alt=""
                      className="w-5 h-5 rounded-full object-cover border border-gray-100 shrink-0" />
                    <div className={`max-w-[75%] px-3 py-1.5 rounded-2xl text-xs leading-snug ${
                      sides[i] === 1
                        ? "bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white rounded-br-sm"
                        : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                      <span className={`block text-[10px] font-semibold ${sides[i] === 1 ? "text-white/80" : "text-gray-500"}`}>
                        {r.user?.name}
                      </span>
                      <span className="line-clamp-2">{r.content}</span>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          {post.my_state?.unread > 0 && (
            <p className="text-[11px] font-semibold text-pink-500 mt-1.5" data-testid="discussion-unread">
              {post.my_state.unread} {t("newInterventions")}
            </p>
          )}
          {post.status === "closed" && (
            <p className="text-[11px] font-semibold text-gray-400 mt-1">{t("discussionClosed")}</p>
          )}
        </div>
      ) : (
        <PinchZoomMedia
          className="w-full bg-black/10 cursor-pointer"
          style={{ aspectRatio: String(mediaRatio), maxHeight: "min(125vw, 78vh)" }}
          onDoubleClick={handleDoubleTap}
          testId={`post-media-${cid}`}
        >
          {post.media_type === "video" ? (
            <PostVideo
              post={{ ...post, media_urls: (post.media_urls || [post.image_url]).map((u) => u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u) }}
              className="w-full h-full"
            />
          ) : (post.media_urls?.length || 1) > 1 ? (
            <>
              <div
                className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full"
                onScroll={(e) => {
                  const i = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth);
                  setCarouselIdx((prev) => (prev === i ? prev : i));
                }}
                data-testid="post-carousel"
              >
                {post.media_urls.map((u, i) => (
                  <img key={i}
                    src={u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u}
                    alt={post.alt_text || ""}
                    className="w-full h-full object-cover shrink-0 snap-center"
                    onLoad={i === 0 ? onMediaLoad : undefined}
                    loading="lazy" />
                ))}
              </div>
              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-semibold z-10" data-testid="carousel-counter">
                {carouselIdx + 1}/{post.media_urls.length}
              </div>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {post.media_urls.map((_, i) => (
                  <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${carouselIdx === i ? "bg-white" : "bg-white/40"}`} />
                ))}
              </div>
            </>
          ) : (
            <img
              src={post.image_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${post.image_url}` : post.image_url}
              alt={post.alt_text || ""}
              className="w-full h-full object-cover"
              loading="lazy"
              onLoad={onMediaLoad}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling?.classList.remove("hidden");
              }}
            />
          )}
          {/* Fallback for broken images */}
          <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-200">
            <div className="text-center text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-xs">{t("imgUnavailable")}</p>
            </div>
          </div>
          {likeAnimation && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10">
              <div className="animate-like-burst drop-shadow-2xl" style={{ filter: "drop-shadow(0 0 20px rgba(249, 115, 22, 0.6))" }}>
                <img src="/images/ade-logo-extracted.png" alt="ADE Like" className="w-32 h-32 object-contain" />
              </div>
            </div>
          )}
        </PinchZoomMedia>
      )}

      {/* Action bar — single fixed order for every content type */}
      <div className="px-4 pt-3 pb-4">
        <div className="flex items-center gap-4">
          <AdLikeIcon filled={isLiked} size={52} data-testid={`ade-like-btn-${cid}`} onClick={handleLikeClick} />
          <GiftButton
            photoId={cid}
            recipientId={author.user_id}
            recipientName={isDiscussion ? author?.name : undefined}
            totalGifts={totalGifts}
            onGiftSent={(amount, data) => {
              if (isDiscussion) {
                setDGifts((g) => g + 1);
                setDGiftValue((v) => v + (data?.coins_spent || 0));
              } else {
                onGiftSent?.(cid, amount, data);
              }
            }}
          />
          <CommentIcon gid={cid} data-testid={isDiscussion ? `discussion-comment-btn-${cid}` : `comment-btn-${cid}`} onClick={handleCommentClick} />
          <ShareIcon gid={cid} title={t("shareAction")} data-testid={isDiscussion ? `discussion-share-btn-${cid}` : `share-btn-${cid}`} onClick={handleShareClick} />
          <div className="flex-1" />
          <button onClick={handleSaveClick} className="group" title={t("saveAction")} data-testid={`save-btn-${cid}`}>
            <svg viewBox="0 0 24 24" className="w-7 h-7 transition-all group-hover:scale-105 group-active:scale-95">
              <defs>
                <linearGradient id={`saveGradient-${cid}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F59E0B" />
                  <stop offset="100%" stopColor="#EF4444" />
                </linearGradient>
              </defs>
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={isSaved ? `url(#saveGradient-${cid})` : "none"}
                stroke={isSaved ? "none" : "#9CA3AF"}
                strokeWidth="2" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {likesCount > 0 && (
          <button onClick={() => onOpenLikes?.(cid)}
            className="text-sm font-semibold text-gray-900 mt-2 hover:text-gray-600 transition-colors text-left block"
            data-testid={`likes-count-${cid}`}>
            {likesCount.toLocaleString()} AdE {likesCount === 1 ? "Like" : "Likes"}
          </button>
        )}

        {post.gifts_visible !== false && (totalGifts > 0 || giftValue > 0) && (
          <GiftCounter value={giftValue} total={totalGifts} onClick={() => onOpenGifts?.(cid)} testId={`gifts-count-${cid}`} />
        )}

        {isDiscussion ? (
          /* Footer — avatars + counters (left), Partecipa (right), like the mockup */
          <div className="flex items-center gap-2 mt-1.5">
            <button onClick={openDiscussion} className="flex items-center gap-2 text-left group/foot flex-1 min-w-0"
              data-testid={`discussion-replies-count-${cid}`}>
              {post.participants_preview?.length > 0 && (
                <span className="flex -space-x-2 shrink-0">
                  {post.participants_preview.slice(0, 4).map((u, i) => (
                    <img key={u.user_id || i} src={u.picture || "/images/default-avatar.png"} alt=""
                      className="w-5 h-5 rounded-full object-cover border-2 border-white" style={{ zIndex: 4 - i }} />
                  ))}
                </span>
              )}
              <span className="text-sm text-gray-400 group-hover/foot:text-gray-600 transition-colors truncate">
                {post.reply_count > 0
                  ? `${post.participant_count} ${post.participant_count === 1 ? t("participantSingular") : t("participants")} · ${post.reply_count} ${post.reply_count === 1 ? t("replySingular") : t("repliesTxt")}`
                  : t("participateComposer")}
              </span>
            </button>
            <button onClick={participate}
              className={`shrink-0 disc-participate-pill ${joined ? "active" : ""}`}
              data-testid={`discussion-participate-btn-${cid}`}>
              {joined ? (
                <span className="inline-flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} /> Partecipi
                </span>
              ) : "Partecipa"}
            </button>
          </div>
        ) : (
          <>
            {post.caption && (
              <p className="text-sm text-gray-900 mt-1">
                <button onClick={() => navigate(`/app/user/${author.user_id}`)} className="font-semibold hover:text-gray-600 mr-1">
                  {author.name}
                </button>
                <CaptionRenderer text={post.caption} className="text-gray-700" />
              </p>
            )}
            {post.comments_count > 3 && (
              <button className="text-sm text-gray-400 mt-1 hover:text-gray-600 transition-colors">
                {t("seeAll")} {post.comments_count} {t("commentsTxt")}
              </button>
            )}
            {post.recent_comments?.length > 0 && (
              <div className="mt-1.5 space-y-1">
                {post.recent_comments.map((comment) => (
                  <p key={comment.comment_id} data-comment-id={comment.comment_id} className="text-sm">
                    <button onClick={() => navigate(`/app/user/${comment.user.user_id}`)}
                      className="font-semibold text-gray-900 hover:text-gray-600 mr-1">
                      {comment.user.name}
                    </button>
                    <ProBadge user={comment.user} size="xs" showLabel={false} className="mr-1 align-middle" />
                    <span className="text-gray-700">{comment.content}</span>
                  </p>
                ))}
              </div>
            )}
            {/* Add comment */}
            <div className="flex items-center gap-3 mt-3">
              <Avatar className="w-7 h-7">
                <AvatarFallback className="bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400 text-xs">Tu</AvatarFallback>
              </Avatar>
              <div className="relative flex-1">
                <MentionSuggestions
                  text={commentText}
                  onPick={(u) => setCommentText(applyMention(commentText, u.username))}
                />
                <Input
                  id={`comment-input-${cid}`}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={t("writeComment")}
                  className="w-full h-8 border-0 bg-transparent text-sm px-0 focus-visible:ring-0 placeholder:text-gray-400"
                  onKeyDown={(e) => e.key === "Enter" && submitComment()}
                />
              </div>
              {commentText.trim() && (
                <button onClick={submitComment}
                  className="text-transparent bg-gradient-to-r from-pink-500 to-orange-500 bg-clip-text font-semibold text-sm">
                  {t("publishAction")}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Discussion contextual menu: Segui / Abbandona / Silenzia / Segnala */}
      {isDiscussion && menuOpen && (
        <div className="fixed inset-0 z-[80] flex items-end" style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }}>
          <div className="w-full rounded-t-3xl p-4 pb-8 safe-bottom space-y-1" data-testid={`discussion-card-menu-${cid}`}
            style={{ background: "rgba(16,12,28,0.96)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(255,255,255,0.25)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-white/25 mx-auto mb-3" />
            {!st.following && !st.muted && (
              <button onClick={() => doState("follow")}
                className="w-full text-left px-4 py-3 text-sm text-white rounded-xl hover:bg-white/5"
                data-testid="card-menu-follow">{t("followTxt")}</button>
            )}
            {(st.following || st.participant || st.muted) && (
              <button onClick={() => doState("abandon")}
                className="w-full text-left px-4 py-3 text-sm text-white rounded-xl hover:bg-white/5"
                data-testid="card-menu-abandon">{t("abandonTxt")}</button>
            )}
            {st.muted ? (
              <button onClick={() => doState("unmute")}
                className="w-full text-left px-4 py-3 text-sm text-white rounded-xl hover:bg-white/5"
                data-testid="card-menu-unmute">{t("unmuteTxt")}</button>
            ) : (
              <button onClick={() => doState("mute")}
                className="w-full text-left px-4 py-3 text-sm text-white rounded-xl hover:bg-white/5"
                data-testid="card-menu-mute">{t("muteTxt")}</button>
            )}
            <button onClick={doReport}
              className="w-full text-left px-4 py-3 text-sm text-red-400 rounded-xl hover:bg-white/5"
              data-testid="card-menu-report">{t("reportTxt")}</button>
          </div>
        </div>
      )}
    </article>
  );
};

/* Square grid tile — a Discussion saved on the profile exactly like a Post in the grid */
export const DiscussionGridTile = ({ d, style }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/app/discussion/${d.id}`)}
      className="aspect-square relative cursor-pointer feed-glass-card discussion-border flex flex-col p-2"
      style={{ borderRadius: 10, ...style }}
      data-testid={`discussion-tile-${d.id}`}
    >
      <span className="text-[6px] font-bold tracking-[0.14em] text-pink-500/80">DISCUSSION</span>
      <p className="text-[9px] font-bold text-gray-900 leading-tight mt-0.5 line-clamp-2">{d.title}</p>
      {d.recent_replies?.length > 0 ? (
        <div className={`mt-1 flex-1 overflow-hidden flex flex-col gap-[2px] ${d.recent_replies.length >= 4 ? "justify-end" : "justify-start"}`}>
          {(() => {
            const replies = d.recent_replies.slice(-5);
            let side = 0;
            const sides = replies.map((r, i) => {
              if (i > 0 && r.user_id !== replies[i - 1].user_id) side = 1 - side;
              return side;
            });
            return replies.map((r, i) => (
              <div key={r.id || i} className={`flex items-end gap-[2px] ${sides[i] === 1 ? "flex-row-reverse" : ""}`}>
                <img src={r.user?.picture || "/images/default-avatar.png"} alt=""
                  className="w-2.5 h-2.5 rounded-full object-cover shrink-0" />
                <span className={`max-w-[80%] px-1 py-[1px] rounded-md text-[7px] leading-tight truncate ${
                  sides[i] === 1
                    ? "bg-gradient-to-br from-purple-600 to-fuchsia-600 text-white rounded-br-[1px]"
                    : "bg-gray-100 text-gray-700 rounded-bl-[1px]"}`}>
                  {r.content}
                </span>
              </div>
            ));
          })()}
        </div>
      ) : (
        <div className="flex-1" />
      )}
      <p className="text-[8px] text-gray-400 mt-0.5 flex items-center gap-0.5">
        <MessageSquare className="w-2.5 h-2.5" strokeWidth={2.4} /> {d.reply_count || 0}
      </p>
    </div>
  );
};
